import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormField, FormGrid, HpButton, HpCardHeader, HpIcon } from '@hp/ui';
import { ContactInputSchema } from '@hp/domain';
import { useCan, useMe } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { PipedriveIcon } from '../../components/ui/icons';
import { pdRecordUrl } from '../../lib/pipedrive';
import { ValidationErrors } from './validation-errors';
import { zodIssueList } from './validation';
import { invalidateProjects, type PdSyncOutcome, type ProjectContact } from './queries';

/** Fields of ContactInput, as validation issues name them (`contacts.fields.*`). */
const CONTACT_FIELDS = ['name', 'email', 'phone', 'companyName', 'companyIco', 'note'] as const;

export function ContactsSection({ projectId, contacts }: { projectId: string; contacts: ProjectContact[] }) {
  const { t } = useTranslation(['projects', 'common']);
  const me = useMe();
  const [editing, setEditing] = useState<ProjectContact | 'new' | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const canEdit = useCan()('projects.edit');

  const pdDomain = me.data?.pipedriveCompanyDomain;

  const remove = useMutation(trpc.projects.contacts.remove.mutationOptions({ onSuccess: invalidateProjects }));

  const pdSync = useMutation(
    trpc.projects.contacts.syncToPipedrive.mutationOptions({
      onSuccess: (outcome: PdSyncOutcome) => {
        void invalidateProjects();
        const actionLabel = (action: string) => t(`contacts.syncAction.${action}`, { defaultValue: action });
        const parts = [
          t('contacts.syncPerson', { action: actionLabel(outcome.person.action), id: outcome.person.pdId }),
        ];
        if (outcome.org) {
          parts.push(t('contacts.syncOrg', { action: actionLabel(outcome.org.action), id: outcome.org.pdId }));
        }
        setSyncNotice(parts.join(', '));
        window.setTimeout(() => setSyncNotice(null), 6000);
      },
    }),
  );

  return (
    <section className="hp-card">
      <HpCardHeader title={t('contacts.title')} subtitle={t('contacts.subtitle')} icon="user">
        {canEdit && (
          <HpButton variant="primary" icon="plus" onClick={() => setEditing('new')}>
            {t('contacts.add')}
          </HpButton>
        )}
      </HpCardHeader>

      <div className="hp-card__body bo-sections">
        <ErrorAlert error={remove.error ?? pdSync.error} />
        {syncNotice && (
          <p className="bo-notice" role="status">
            {syncNotice}
          </p>
        )}

        {contacts.length === 0 && editing === null && <p className="bo-muted">{t('contacts.empty')}</p>}

        {contacts.length > 0 && (
          <ul className="bo-people">
            {contacts.map((contact) => (
              <li key={contact.id} className="bo-person">
                <div className="bo-row">
                  <strong>{contact.name}</strong>
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="hp-detail-action hp-detail-action--text">
                      {contact.email}
                    </a>
                  )}
                  {contact.phone && <span className="bo-mono bo-muted">{contact.phone}</span>}
                  {contact.companyName && (
                    <span className="bo-muted">
                      {contact.companyName}
                      {contact.companyIco && (
                        <span className="bo-mono"> · {t('contacts.ico', { ico: contact.companyIco })}</span>
                      )}
                    </span>
                  )}
                  <span className="bo-grow-end bo-row">
                    {contact.pdPersonId !== null ? (
                      <>
                        <a
                          href={pdRecordUrl(pdDomain, 'person', contact.pdPersonId)}
                          target="_blank"
                          rel="noreferrer"
                          className="hp-detail-action hp-detail-action--text bo-pd-link"
                          title={t('contacts.openPerson')}
                        >
                          <PipedriveIcon />
                          <span>{t('contacts.person')}</span>
                          <HpIcon name="arrowUpRight" />
                        </a>
                        {contact.pdOrgId !== null && (
                          <a
                            href={pdRecordUrl(pdDomain, 'organization', contact.pdOrgId)}
                            target="_blank"
                            rel="noreferrer"
                            className="hp-detail-action hp-detail-action--text bo-pd-link"
                            title={t('contacts.openOrg')}
                          >
                            <PipedriveIcon />
                            <span>{t('contacts.org')}</span>
                            <HpIcon name="arrowUpRight" />
                          </a>
                        )}
                      </>
                    ) : (
                      <HpButton
                        className="bo-button--sm"
                        variant="secondary"
                        icon="refresh"
                        busy={pdSync.isPending && pdSync.variables?.contactId === contact.id}
                        disabled={pdSync.isPending || !canEdit}
                        title={t('contacts.syncTitle')}
                        onClick={() => pdSync.mutate({ projectId, contactId: contact.id })}
                      >
                        {pdSync.isPending ? t('contacts.syncing') : t('contacts.sync')}
                      </HpButton>
                    )}
                    <HpButton
                      className="bo-button--sm"
                      icon="edit"
                      disabled={!canEdit}
                      onClick={() => setEditing(contact)}
                    >
                      {t('common:actions.edit')}
                    </HpButton>
                    <HpButton
                      className="bo-button--sm bo-danger"
                      icon="trash"
                      busy={remove.isPending && remove.variables?.contactId === contact.id}
                      disabled={remove.isPending || !canEdit}
                      onClick={() => {
                        if (window.confirm(t('contacts.confirmDelete', { name: contact.name })))
                          remove.mutate({ projectId, contactId: contact.id });
                      }}
                    >
                      {t('common:actions.delete')}
                    </HpButton>
                  </span>
                </div>
                {contact.note && <p className="bo-muted">{contact.note}</p>}
              </li>
            ))}
          </ul>
        )}

        {editing !== null && (
          <ContactFormPanel
            key={editing === 'new' ? 'new' : editing.id}
            projectId={projectId}
            contact={editing === 'new' ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
      </div>
    </section>
  );
}

function ContactFormPanel({
  projectId,
  contact,
  onClose,
}: {
  projectId: string;
  contact: ProjectContact | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const [name, setName] = useState(contact?.name ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [companyName, setCompanyName] = useState(contact?.companyName ?? '');
  const [companyIco, setCompanyIco] = useState(contact?.companyIco ?? '');
  const [note, setNote] = useState(contact?.note ?? '');
  const [issues, setIssues] = useState<string[]>([]);

  const onSaved = () => {
    void invalidateProjects();
    onClose();
  };
  const create = useMutation(trpc.projects.contacts.create.mutationOptions({ onSuccess: onSaved }));
  const update = useMutation(trpc.projects.contacts.update.mutationOptions({ onSuccess: onSaved }));
  const save = { isPending: create.isPending || update.isPending, error: create.error ?? update.error };

  function submit() {
    const empty = (s: string) => (s.trim() === '' ? null : s.trim());
    const parsed = ContactInputSchema.safeParse({
      name: name.trim(),
      email: empty(email),
      phone: empty(phone),
      companyName: empty(companyName),
      companyIco: empty(companyIco),
      note: empty(note),
    });
    if (!parsed.success) {
      const labels = Object.fromEntries(CONTACT_FIELDS.map((field) => [field, t(`contacts.fields.${field}`)]));
      setIssues(zodIssueList(parsed.error, labels));
      return;
    }
    setIssues([]);
    if (contact === null) create.mutate({ projectId, contact: parsed.data });
    else update.mutate({ projectId, contactId: contact.id, contact: parsed.data });
  }

  return (
    <div className="bo-subform">
      <h3 className="bo-subform__title">
        {contact === null ? t('contacts.form.new') : t('contacts.form.edit', { name: contact.name })}
      </h3>
      <FormGrid className="bo-form-grid--3">
        <FormField label={t('contacts.fields.name')}>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label={t('contacts.fields.email')}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label={t('contacts.fields.phone')}>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </FormField>
        <FormField label={t('contacts.fields.companyName')}>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </FormField>
        <FormField label={t('contacts.fields.companyIco')}>
          <input
            placeholder={t('contacts.form.icoPlaceholder')}
            value={companyIco}
            onChange={(e) => setCompanyIco(e.target.value)}
          />
        </FormField>
        <FormField label={t('contacts.fields.note')}>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </FormField>
      </FormGrid>
      <div className="bo-row">
        <HpButton variant="primary" icon="check" busy={save.isPending} disabled={name.trim() === ''} onClick={submit}>
          {save.isPending
            ? t('contacts.form.saving')
            : contact === null
              ? t('contacts.form.add')
              : t('contacts.form.save')}
        </HpButton>
        <HpButton onClick={onClose}>{t('common:actions.cancel')}</HpButton>
      </div>
      <ValidationErrors issues={issues} />
      <ErrorAlert error={save.error} />
    </div>
  );
}
